import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { useLocalePrefix } from '@/lib/hooks/useLocaleNavigate';
import { ProtectedRoute } from './components/ProtectedRoute';
import { LocaleLayout } from './components/LocaleLayout';
// HR-03 — Payroll screens ask for the password again (a second check only).
import { PayrollLock } from '@/components/salary/PayrollLock';

const LoginPage = lazy(() => import('@/app/[locale]/login/page'));
const Dashboard = lazy(() => import('@/app/[locale]/dashboard/page'));
const DashboardOutlet = lazy(() => import('@/app/[locale]/dashboard/outlet/page'));
const DashboardOffice = lazy(() => import('@/app/[locale]/dashboard/office/page'));
const DashboardOfficeRules = lazy(() => import('@/app/[locale]/dashboard/office/rules/page'));
const DashboardOfficeTasks = lazy(() => import('@/app/[locale]/dashboard/office/tasks/page'));
const DashboardManagement = lazy(() => import('@/app/[locale]/dashboard/management/page'));
const DashboardBusiness = lazy(() => import('@/app/[locale]/dashboard/business/page'));
const Schedule = lazy(() => import('@/app/[locale]/schedule/page'));
const Customers = lazy(() => import('@/app/[locale]/customers/page'));
const CreateCustomer = lazy(() => import('@/app/[locale]/customers/create/page'));
const MergeCustomer = lazy(() => import('@/app/[locale]/customers/merge/page'));
const CustomerFields = lazy(() => import('@/app/[locale]/customers/configurations/fields/page'));
const CustomerGroups = lazy(() => import('@/app/[locale]/customers/groups/page'));
const CustomerReports = lazy(() => import('@/app/[locale]/customers/reports/page'));
const CustomerReportsByGender = lazy(() => import('@/app/[locale]/customers/reports/by-gender/page'));
const CustomerReportsByMembership = lazy(
  () => import('@/app/[locale]/customers/reports/by-membership/page')
);
const CustomerReportsByEnrollment = lazy(
  () => import('@/app/[locale]/customers/reports/by-enrollment/page')
);
const CustomerReportsByBirthday = lazy(
  () => import('@/app/[locale]/customers/reports/by-birthday/page')
);
const CustomerReportsInactive = lazy(() => import('@/app/[locale]/customers/reports/inactive/page'));
const CustomerReportsByAgeGroup = lazy(
  () => import('@/app/[locale]/customers/reports/by-age-group/page')
);
const Leads = lazy(() => import('@/app/[locale]/customers/leads/page'));
const LeadsSettings = lazy(() => import('@/app/[locale]/customers/leads/settings/page'));
const LeadsListResults = lazy(() => import('@/app/[locale]/customers/leads/reports/list-results/page'));
const MessageTemplates = lazy(() => import('@/app/[locale]/customers/leads/templates/page'));
const LeadsFollowUp = lazy(() => import('@/app/[locale]/customers/leads/follow-up/page'));
const ConsentDataPrivacy = lazy(() => import('@/app/[locale]/customers/consent/data-privacy/page'));
const ConsentMarketing = lazy(() => import('@/app/[locale]/customers/consent/marketing/page'));
const FormTemplates = lazy(() => import('@/app/[locale]/customers/forms/templates/page'));
const FormTemplateEditor = lazy(() => import('@/app/[locale]/customers/forms/templates/[id]/page'));
const FormResponses = lazy(() => import('@/app/[locale]/customers/forms/responses/page'));
// GEN-06 — the same Forms engine under Sales (/finance), HR and Marketing.
const SalesFormTemplates = lazy(() => import('@/app/[locale]/finance/forms/templates/page'));
const SalesFormTemplateEditor = lazy(
  () => import('@/app/[locale]/finance/forms/templates/[id]/page')
);
const HrFormTemplates = lazy(() => import('@/app/[locale]/hr/forms/templates/page'));
const HrFormTemplateEditor = lazy(() => import('@/app/[locale]/hr/forms/templates/[id]/page'));
const MarketingFormTemplates = lazy(() => import('@/app/[locale]/marketing/forms/templates/page'));
const MarketingFormTemplateEditor = lazy(
  () => import('@/app/[locale]/marketing/forms/templates/[id]/page')
);
const CustomersFillAForm = lazy(() => import('@/app/[locale]/customers/forms/fill/page'));
const CustomersFillForm = lazy(
  () => import('@/app/[locale]/customers/forms/fill/[templateId]/page')
);
const SalesFillAForm = lazy(() => import('@/app/[locale]/finance/forms/fill/page'));
const SalesFillForm = lazy(() => import('@/app/[locale]/finance/forms/fill/[templateId]/page'));
const HrFillAForm = lazy(() => import('@/app/[locale]/hr/forms/fill/page'));
const HrFillForm = lazy(() => import('@/app/[locale]/hr/forms/fill/[templateId]/page'));
const MarketingFillAForm = lazy(() => import('@/app/[locale]/marketing/forms/fill/page'));
const MarketingFillForm = lazy(
  () => import('@/app/[locale]/marketing/forms/fill/[templateId]/page')
);
const SalesFormResponses = lazy(() => import('@/app/[locale]/finance/forms/responses/page'));
const HrFormResponses = lazy(() => import('@/app/[locale]/hr/forms/responses/page'));
const MarketingFormResponses = lazy(() => import('@/app/[locale]/marketing/forms/responses/page'));
const YaksonAiHistory = lazy(() => import('@/app/[locale]/hr/yakson-ai/history/page'));
const YaksonAiKnowledge = lazy(() => import('@/app/[locale]/hr/yakson-ai/knowledge/page'));
const YaksonAiQa = lazy(() => import('@/app/[locale]/hr/yakson-ai/qa/page'));
const ConsentTablet = lazy(() => import('@/app/[locale]/customers/[id]/consent-tablet/page'));
const SurveyTablet = lazy(() => import('@/app/[locale]/customers/[id]/survey-tablet/page'));
const Staff = lazy(() => import('@/app/[locale]/staff/page'));
const StaffProfile = lazy(() => import('@/app/[locale]/staff/[id]/page'));
const MyProfile = lazy(() => import('@/app/[locale]/my-profile/page'));
const Treatments = lazy(() => import('@/app/[locale]/treatments/page'));
const Products = lazy(() => import('@/app/[locale]/inventory/products/page'));
const Stock = lazy(() => import('@/app/[locale]/inventory/stock/page'));
const MarketingPromotion = lazy(() => import('@/app/[locale]/marketing/promotion/page'));
const MarketingOutletAuthority = lazy(() => import('@/app/[locale]/marketing/outlet-authority/page'));
const MarketingMedia = lazy(() => import('@/app/[locale]/marketing/media/page'));
const InventoryLevel = lazy(() => import('@/app/[locale]/inventory/inventory-level/page'));
const Stocktake = lazy(() => import('@/app/[locale]/inventory/stocktake/page'));
const LowStockReorder = lazy(() => import('@/app/[locale]/inventory/reports/low-stock-reorder/page'));
const UsageVsTreatments = lazy(() => import('@/app/[locale]/inventory/reports/usage-vs-treatments/page'));
const LossSummary = lazy(() => import('@/app/[locale]/inventory/reports/loss-summary/page'));
const StockValue = lazy(() => import('@/app/[locale]/inventory/reports/stock-value/page'));
const StockMovementSummary = lazy(() => import('@/app/[locale]/inventory/reports/stock-movement-summary/page'));
const TreatmentProductStatus = lazy(() => import('@/app/[locale]/inventory/reports/treatment-product-status/page'));
const MarketingEnrollmentByMedia = lazy(() => import('@/app/[locale]/marketing/reports/customer-enrollment-by-media/page'));
const MarketingEnrollmentByPromotion = lazy(() => import('@/app/[locale]/marketing/reports/customer-enrollment-by-promotion/page'));
const Rooms = lazy(() => import('@/app/[locale]/rooms/page'));
const Commission = lazy(() => import('@/app/[locale]/commission/page'));
const DailyReport = lazy(() => import('@/app/[locale]/daily-report/page'));
const Mediation = lazy(() => import('@/app/[locale]/mediation/page'));
const Questionnaires = lazy(() => import('@/app/[locale]/questionnaires/page'));
const CustomerTickets = lazy(() => import('@/app/[locale]/customer-tickets/page'));
const MeetingLog = lazy(() => import('@/app/[locale]/meetings/log/page'));
const TrainingLog = lazy(() => import('@/app/[locale]/training/log/page'));
const ReviewSubmission = lazy(() => import('@/app/[locale]/performance-review/submission/page'));
const ReviewResponses = lazy(() => import('@/app/[locale]/performance-review/responses/page'));
const TrainingSchedule = lazy(() => import('@/app/[locale]/training/schedule/page'));
const TrainingMatrix = lazy(() => import('@/app/[locale]/training/matrix/page'));
const TrainingSopLibrary = lazy(() => import('@/app/[locale]/training/sop-library/page'));
const PromotionStatus = lazy(() => import('@/app/[locale]/staff-promotion/status/page'));
const PromotionHistory = lazy(() => import('@/app/[locale]/staff-promotion/history/page'));
const PromotionTemplate = lazy(() => import('@/app/[locale]/staff-promotion/template/page'));
const TrainingSkillMatrix = lazy(() => import('@/app/[locale]/training/skill-matrix/page'));
const Reports = lazy(() => import('@/app/[locale]/reports/page'));
const ReportsDutyRoster = lazy(() => import('@/app/[locale]/reports/duty-roster/page'));
const ReportsWorkingHours = lazy(() => import('@/app/[locale]/reports/working-hours/page'));
const ReportsRoomUtilization = lazy(() => import('@/app/[locale]/reports/room-utilization/page'));
const ReportsAutoUsage = lazy(() => import('@/app/[locale]/reports/auto-usage/page'));
const ReportsOperationsOverview = lazy(
  () => import('@/app/[locale]/reports/operations-overview/page')
);
// N-06 replaced "Appointments by Outlet", "New Customer Appointments" and
// "Existing Customer Appointments" with one Appointments report.
const ReportsAppointments = lazy(() => import('@/app/[locale]/reports/appointments/page'));
const ReportsCancellations = lazy(() => import('@/app/[locale]/reports/cancellations/page'));
const ReportsConsultations = lazy(() => import('@/components/reports/ConsultationResultsPage'));
const ReportsQuestionnaires = lazy(() => import('@/app/[locale]/reports/questionnaires/page'));
const Settings = lazy(() => import('@/app/[locale]/settings/page'));
const AccessSettings = lazy(() => import('@/app/[locale]/settings/access/page'));
const RolesSettings = lazy(() => import('@/app/[locale]/settings/roles/page'));
const ReportRightsSettings = lazy(() => import('@/app/[locale]/settings/roles/reports/page'));
const RoleRightsSettings = lazy(() => import('@/app/[locale]/settings/roles/[id]/rights/page'));
const LeaveSettings = lazy(() => import('@/app/[locale]/settings/leave/page'));
const SalarySettings = lazy(() => import('@/app/[locale]/settings/salary/page'));
const ProgramStepMapping = lazy(() => import('@/app/[locale]/settings/program-step-mapping/page'));
const AttendanceReport = lazy(() => import('@/app/[locale]/attendance/report/page'));
const LeaveCalendar = lazy(() => import('@/app/[locale]/leave/calendar/page'));
const LeaveYtd = lazy(() => import('@/app/[locale]/leave/ytd/page'));
const LeaveHistory = lazy(() => import('@/app/[locale]/leave/history/page'));
const LeaveApprovers = lazy(() => import('@/app/[locale]/leave/approvers/page'));
const CustomersByMedia = lazy(() => import('@/app/[locale]/customers/reports/by-media/page'));
const CustomersByMarketing = lazy(() => import('@/app/[locale]/customers/reports/by-marketing/page'));
const SalesByMedia = lazy(() => import('@/app/[locale]/marketing/reports/sales-by-media/page'));
const SalesByMarketing = lazy(() => import('@/app/[locale]/marketing/reports/sales-by-marketing/page'));
const ShiftTypeSettings = lazy(() => import('@/app/[locale]/settings/shift-types/page'));
const RosterChangeLog = lazy(() => import('@/app/[locale]/roster/change-log/page'));
const CreditRatesSettings = lazy(() => import('@/app/[locale]/settings/credits/page'));
const RestTimePage = lazy(() => import('@/app/[locale]/operations/rest-time/page'));
const CancellationReasonsPage = lazy(() => import('@/app/[locale]/operations/cancellation-reasons/page'));
const CreditTypesPage = lazy(() => import('@/app/[locale]/finance/config/credit-types/page'));
const ApprovalSettingsPage = lazy(() => import('@/app/[locale]/finance/config/approval-settings/page'));
const DropdownListsSettings = lazy(() => import('@/app/[locale]/settings/dropdown-lists/page'));
// CUS-01 / GEN-06-12 — the consent tablet: HQ registers it, the iPad pairs on
// /tablet (no staff login — it holds a device token only).
const TabletsSettings = lazy(() => import('@/app/[locale]/settings/tablets/page'));
const OutletsSettings = lazy(() => import('@/app/[locale]/company/outlets/page'));
const TabletPage = lazy(() => import('@/app/[locale]/tablet/page'));
const Leave = lazy(() => import('@/app/[locale]/leave/page'));
const LeaveAdmin = lazy(() => import('@/app/[locale]/leave/admin/page'));
const LeaveTypes = lazy(() => import('@/app/[locale]/leave/types/page'));
const StaffClaimSubmission = lazy(() => import('@/app/[locale]/hr/claims/page'));
const StaffClaimResponses = lazy(() => import('@/app/[locale]/hr/claims/responses/page'));
const AuditTrail = lazy(() => import('@/app/[locale]/hr/audit-trail/page'));
const Salary = lazy(() => import('@/app/[locale]/salary/page'));
const SalaryMe = lazy(() => import('@/app/[locale]/salary/me/page'));
const FinanceCreditNote = lazy(() => import('@/app/[locale]/finance/pos/credit-note/page'));
const FinanceBalancePayment = lazy(() => import('@/app/[locale]/finance/pos/balance-payment/page'));
const FinanceCreateUsage = lazy(() => import('@/app/[locale]/finance/pos/create-usage/page'));
const FinanceVoidUsage = lazy(() => import('@/app/[locale]/finance/pos/void-usage/page'));
const FinanceCreateInvoice = lazy(() => import('@/app/[locale]/finance/pos/create-invoice/page'));
const FinanceContraInvoice = lazy(() => import('@/app/[locale]/finance/pos/contra-invoice/page'));
const FinanceUpgradeInvoice = lazy(
  () => import('@/app/[locale]/finance/pos/upgrade-invoice/page')
);
const FinanceExtensionRequest = lazy(() => import('@/app/[locale]/finance/pos/extension/page'));
const FinanceHolderTransfer = lazy(() => import('@/app/[locale]/finance/pos/transfer/page'));
const FinanceRefundRequest = lazy(() => import('@/app/[locale]/finance/pos/refund/page'));
const FinancePreviewInvoice = lazy(() => import('@/app/[locale]/finance/pos/preview-invoice/page'));
const FinanceApprovals = lazy(() => import('@/app/[locale]/finance/approvals/page'));
const FinanceDailyClose = lazy(() => import('@/app/[locale]/finance/daily-close/page'));
const FinanceIHIP = lazy(() => import('@/app/[locale]/finance/ihip/page'));
const FinanceReports = lazy(() => import('@/app/[locale]/finance/reports/page'));
// SPEC-REPORTS-SALES — the twelve SM sales reports, one route each so the nav
// can link straight to a report instead of a tab inside a combined page.
const RptSalesByCustomer = lazy(
  () => import('@/app/[locale]/finance/reports/sales-by-customer/page')
);
const RptCollectionByPaymentTypes = lazy(
  () => import('@/app/[locale]/finance/reports/collection-by-payment-types/page')
);
const RptCollectionByCustomer = lazy(
  () => import('@/app/[locale]/finance/reports/collection-by-customer/page')
);
const RptTreatmentDoneByOutlets = lazy(
  () => import('@/app/[locale]/finance/reports/treatment-done-by-outlets/page')
);
const RptConversion = lazy(() => import('@/app/[locale]/finance/reports/conversion/page'));
const RptCollection = lazy(() => import('@/app/[locale]/finance/reports/collection/page'));
const RptSalesByStaff = lazy(() => import('@/app/[locale]/finance/reports/sales-by-staff/page'));
const RptConsultationResults = lazy(() => import('@/app/[locale]/finance/reports/consultation-results/page'));
const RptMoneyDueRisk = lazy(() => import('@/app/[locale]/finance/reports/money-due-risk/page'));
const RptTopCustomersByLimit = lazy(() => import('@/app/[locale]/finance/reports/top-customers-by-limit/page'));
const RptTopSellers = lazy(() => import('@/app/[locale]/finance/reports/top-sellers/page'));
const RptDiscountsRefundsVoids = lazy(() => import('@/app/[locale]/finance/reports/discounts-refunds-voids/page'));
const RptCreditNotes = lazy(() => import('@/app/[locale]/finance/reports/credit-notes/page'));
const RptGst = lazy(() => import('@/app/[locale]/finance/reports/gst/page'));
const RptItemSoldByOutlet = lazy(
  () => import('@/app/[locale]/finance/reports/item-sold-by-outlet/page')
);
const RptItemSoldByDate = lazy(
  () => import('@/app/[locale]/finance/reports/item-sold-by-date/page')
);
const RptAlaCarteByOutlet = lazy(
  () => import('@/app/[locale]/finance/reports/ala-carte-by-outlet/page')
);
const RptAlaCarteByBeautician = lazy(
  () => import('@/app/[locale]/finance/reports/ala-carte-by-beautician/page')
);
const RptUnearnedRevenue = lazy(
  () => import('@/app/[locale]/finance/reports/unearned-revenue/page')
);
const RptVoidUsages = lazy(() => import('@/app/[locale]/finance/reports/void-usages/page'));
const RptExpiredInvoices = lazy(
  () => import('@/app/[locale]/finance/reports/expired-invoices/page')
);
const RptItemsSoldOutletCollection = lazy(
  () => import('@/app/[locale]/finance/reports/items-sold-outlet-collection/page')
);
const FinanceSales = lazy(() => import('@/app/[locale]/finance/sales/page'));
const FinanceConfigPaymentTypes = lazy(() => import('@/app/[locale]/finance/config/payment-types/page'));
const FinanceConfigPaymentKinds = lazy(() => import('@/app/[locale]/finance/config/payment-kinds/page'));
const FinanceConfigTaxes = lazy(() => import('@/app/[locale]/finance/config/taxes/page'));
// SPEC-SALES-NAV — Search. The three Unearned Revenue reports it named are
// declared with the rest of SPEC-REPORTS-SALES above (Rpt* consts).
const FinanceSearchInvoices = lazy(() => import('@/app/[locale]/finance/search-invoices/page'));
const FinanceSearchUsages = lazy(() => import('@/app/[locale]/finance/search-usages/page'));
const FinanceSearchCreditNotes = lazy(() => import('@/app/[locale]/finance/search-credit-notes/page'));
const FinanceApprovalRequests = lazy(() => import('@/app/[locale]/finance/approvals/requests/page'));
const HrDamageReport = lazy(() => import('@/app/[locale]/hr/forms/damage-report/page'));
const HrComplimentFeedback = lazy(() => import('@/app/[locale]/hr/forms/compliment-feedback/page'));
const HrFormSubmissions = lazy(() => import('@/app/[locale]/hr/forms/submissions/page'));
const FinanceXero = lazy(() => import('@/app/[locale]/finance/xero/page'));
const LabDrawing = lazy(() => import('@/app/[locale]/lab/drawing/page'));

/**
 * N-06 — sends a retired appointment report to the combined one, preset to the
 * customer type it used to hardcode. The target is spelled out with the locale
 * prefix because a relative `to` would resolve underneath the retired path.
 */
function RedirectToAppointmentsReport({
  customerType,
}: {
  customerType?: 'NEW' | 'EXISTING';
}) {
  const prefix = useLocalePrefix();
  const query = customerType ? `?customerType=${customerType}` : '';
  return <Navigate to={`${prefix}/reports/appointments${query}`} replace />;
}

/**
 * SAL-14-03 — every link or button that opened the retired Point of Sale screen
 * (/finance/pos) opens New Invoice. Any query string is carried over.
 */
function RedirectToNewInvoice() {
  const prefix = useLocalePrefix();
  const { search } = useLocation();
  return <Navigate to={`${prefix}/finance/pos/create-invoice${search}`} replace />;
}

/** Settings › Outlets moved to HR › Manage Company › Outlets (24 Oct 2026). */
function RedirectToCompanyOutlets() {
  const prefix = useLocalePrefix();
  return <Navigate to={`${prefix}/company/outlets`} replace />;
}

/**
 * HR-11 — Users is merged into HR › Staff. Old links (/users, /settings/users,
 * Role › 'View users with this role') land on the one list; the query string
 * (?roleId=…&from=roles) is carried over.
 */
function RedirectToStaff() {
  const prefix = useLocalePrefix();
  const { search } = useLocation();
  return <Navigate to={`${prefix}/staff${search}`} replace />;
}

/**
 * CUS-05-14 — Requests & Complaints is opened from the Fill a form card, so its
 * screen lives under Fill a form (the menu path and the highlight follow). The
 * old address keeps working for saved links, the dashboard strip and the card.
 */
function RedirectToRequestsComplaints() {
  const prefix = useLocalePrefix();
  const { search } = useLocation();
  return <Navigate to={`${prefix}/customers/forms/fill/requests-complaints${search}`} replace />;
}

/**
 * GEN-12-19 — Unanswered is a filter inside History, not a tab. The old address
 * opens History with that filter on.
 */
function RedirectToUnanswered() {
  const prefix = useLocalePrefix();
  return <Navigate to={`${prefix}/hr/yakson-ai/history?filter=unanswered`} replace />;
}

/** Q&A setup has its own address (sidebar item); the old History › Q&A address still works. */
function RedirectToQa() {
  const prefix = useLocalePrefix();
  return <Navigate to={`${prefix}/hr/yakson-ai/qa`} replace />;
}

/**
 * GEN-06-22 — Forms › Approvals is no longer a page: its forms wait in the
 * 'Waiting for approval' tab of the same menu's Form Responses. Old links open
 * that tab (users without the right see Form Responses as usual).
 */
function RedirectToWaitingForms({ to }: { to: string }) {
  const prefix = useLocalePrefix();
  return <Navigate to={`${prefix}${to}?tab=waiting`} replace />;
}

/**
 * HR-16 — Meeting Deck and Meeting Minutes are merged into Meeting Log. The old
 * addresses (/meetings, /meetings/deck) keep working for saved links.
 */
function RedirectToMeetingLog() {
  const prefix = useLocalePrefix();
  const { search } = useLocation();
  return <Navigate to={`${prefix}/meetings/log${search}`} replace />;
}

export function AppRoutes() {
  return (
    <Suspense fallback={null}>
      <Routes>
        <Route index element={<Navigate to="/en/dashboard" replace />} />

        <Route path=":locale">
          <Route path="login" element={<LoginPage />} />
          <Route path="tablet" element={<TabletPage />} />

          <Route element={<ProtectedRoute />}>
            <Route element={<LocaleLayout />}>
              <Route path="dashboard" element={<Dashboard />} />
              {/* DSH-00 / GEN-10 — one route per view; each checks its own key. */}
              <Route path="dashboard/outlet" element={<DashboardOutlet />} />
              <Route path="dashboard/office" element={<DashboardOffice />} />
              {/* DSH-21 screen 2c — My task rules. */}
              <Route path="dashboard/office/rules" element={<DashboardOfficeRules />} />
              {/* DSH-09 — the task sheet. */}
              <Route path="dashboard/office/tasks" element={<DashboardOfficeTasks />} />
              <Route path="dashboard/management" element={<DashboardManagement />} />
              <Route path="dashboard/business" element={<DashboardBusiness />} />
              <Route path="schedule" element={<Schedule />} />

              <Route path="customers" element={<Customers />} />
              {/* The combined overview stays at /customers/reports; Angela's
                  Aug 2026 menu gives each breakdown its own page below it. */}
              <Route path="customers/reports" element={<CustomerReports />} />
              <Route path="customers/reports/by-gender" element={<CustomerReportsByGender />} />
              <Route
                path="customers/reports/by-membership"
                element={<CustomerReportsByMembership />}
              />
              <Route
                path="customers/reports/by-enrollment"
                element={<CustomerReportsByEnrollment />}
              />
              <Route path="customers/reports/by-age-group" element={<CustomerReportsByAgeGroup />} />
              <Route path="customers/reports/by-birthday" element={<CustomerReportsByBirthday />} />
              <Route path="customers/create" element={<CreateCustomer />} />
              <Route path="customers/merge" element={<MergeCustomer />} />
              <Route path="customers/configurations/fields" element={<CustomerFields />} />
              <Route path="customers/groups" element={<CustomerGroups />} />
              {/* CUS-09-01: the old Customer Category address keeps working. */}
              <Route path="customers/categories" element={<CustomerGroups />} />
              <Route path="customers/consent/data-privacy" element={<ConsentDataPrivacy />} />
              <Route path="customers/consent/marketing" element={<ConsentMarketing />} />
              {/* `:id` is either `new` (create) or a template id (edit). */}
              <Route path="customers/forms/templates" element={<FormTemplates />} />
              <Route path="customers/forms/templates/:id" element={<FormTemplateEditor />} />
              <Route path="customers/forms/responses" element={<FormResponses />} />
              <Route path="customers/reports/inactive" element={<CustomerReportsInactive />} />
              <Route path="customers/leads" element={<Leads />} />
              <Route path="customers/leads/settings" element={<LeadsSettings />} />
              <Route path="customers/leads/reports/list-results" element={<LeadsListResults />} />
              <Route path="customers/leads/templates" element={<MessageTemplates />} />
              <Route path="customers/leads/follow-up" element={<LeadsFollowUp />} />
              <Route path="customers/:id/consent-tablet" element={<ConsentTablet />} />
              <Route path="customers/:id/survey-tablet" element={<SurveyTablet />} />

              <Route path="staff" element={<Staff />} />
              <Route path="staff/:id" element={<StaffProfile />} />
              <Route path="my-profile" element={<MyProfile />} />

              <Route path="treatments" element={<Treatments />} />
              <Route path="inventory/products" element={<Products />} />
              <Route path="inventory/stock" element={<Stock />} />
              <Route path="inventory/inventory-level" element={<InventoryLevel />} />
              <Route path="inventory/stocktake" element={<Stocktake />} />
              <Route path="inventory/reports/low-stock-reorder" element={<LowStockReorder />} />
              <Route path="inventory/reports/usage-vs-treatments" element={<UsageVsTreatments />} />
              <Route path="inventory/reports/stock-value" element={<StockValue />} />
              <Route path="inventory/reports/loss-summary" element={<LossSummary />} />
              <Route path="inventory/reports/stock-movement-summary" element={<StockMovementSummary />} />
              <Route path="inventory/reports/treatment-product-status" element={<TreatmentProductStatus />} />
              <Route path="marketing/promotion" element={<MarketingPromotion />} />
              <Route path="marketing/outlet-authority" element={<MarketingOutletAuthority />} />
              <Route path="marketing/media" element={<MarketingMedia />} />
              <Route path="marketing/reports/customer-enrollment-by-media" element={<MarketingEnrollmentByMedia />} />
              <Route path="marketing/reports/customer-enrollment-by-promotion" element={<MarketingEnrollmentByPromotion />} />
              <Route path="rooms" element={<Rooms />} />
              <Route path="commission" element={<PayrollLock screen="commission"><Commission /></PayrollLock>} />
              <Route path="daily-report" element={<DailyReport />} />
              <Route path="mediation" element={<PayrollLock screen="mediation"><Mediation /></PayrollLock>} />
              <Route path="questionnaires" element={<Questionnaires />} />
              <Route path="customer-tickets" element={<RedirectToRequestsComplaints />} />
              <Route path="customers/forms/fill/requests-complaints" element={<CustomerTickets />} />
              <Route path="meetings" element={<RedirectToMeetingLog />} />
              <Route path="meetings/log" element={<MeetingLog />} />
              <Route path="meetings/deck" element={<RedirectToMeetingLog />} />
              <Route path="training/log" element={<TrainingLog />} />
              <Route path="performance-review/submission" element={<ReviewSubmission />} />
              <Route path="performance-review/responses" element={<ReviewResponses />} />
              <Route path="training/schedule" element={<TrainingSchedule />} />
              <Route path="training/matrix" element={<TrainingMatrix />} />
              <Route path="training/sop-library" element={<TrainingSopLibrary />} />
              <Route path="staff-promotion/status" element={<PromotionStatus />} />
              <Route path="staff-promotion/history" element={<PromotionHistory />} />
              <Route path="staff-promotion/template" element={<PromotionTemplate />} />
              <Route path="training/skill-matrix" element={<TrainingSkillMatrix />} />
              {/* The original five-tab page stays at /reports for existing
                  links (HR → Performance still deep-links ?tab=staff); each
                  tab also has its own page under it. */}
              <Route path="reports" element={<Reports />} />
              <Route path="reports/appointments" element={<ReportsAppointments />} />
              <Route path="reports/cancellations" element={<ReportsCancellations />} />
              <Route path="reports/consultations" element={<ReportsConsultations />} />
              <Route path="reports/questionnaires" element={<ReportsQuestionnaires />} />
              <Route path="reports/working-hours" element={<ReportsWorkingHours />} />
              <Route path="reports/duty-roster" element={<ReportsDutyRoster />} />
              <Route path="reports/room-utilization" element={<ReportsRoomUtilization />} />
              <Route path="reports/auto-usage" element={<ReportsAutoUsage />} />
              <Route path="reports/operations-overview" element={<ReportsOperationsOverview />} />
              {/* N-06 — the three retired appointment reports. Kept as
                  redirects so existing bookmarks land on the combined report
                  with the filter each of them used to hardcode. */}
              <Route
                path="reports/appointments-by-outlet"
                element={<RedirectToAppointmentsReport />}
              />
              <Route
                path="reports/new-customer-appointments"
                element={<RedirectToAppointmentsReport customerType="NEW" />}
              />
              <Route
                path="reports/existing-customer-appointments"
                element={<RedirectToAppointmentsReport customerType="EXISTING" />}
              />
              <Route path="users" element={<RedirectToStaff />} />

              <Route path="settings" element={<Settings />} />
              <Route path="settings/access" element={<AccessSettings />} />
              <Route path="settings/roles" element={<RolesSettings />} />
              <Route path="settings/roles/reports" element={<ReportRightsSettings />} />
              <Route path="settings/roles/:id/rights" element={<RoleRightsSettings />} />
              <Route path="settings/users" element={<RedirectToStaff />} />
              <Route path="settings/leave" element={<LeaveSettings />} />
              <Route path="settings/salary" element={<SalarySettings />} />
              <Route path="settings/program-step-mapping" element={<ProgramStepMapping />} />
              <Route path="settings/shift-types" element={<ShiftTypeSettings />} />
              <Route path="roster/change-log" element={<RosterChangeLog />} />
              <Route path="settings/credits" element={<CreditRatesSettings />} />
              <Route path="operations/rest-time" element={<RestTimePage />} />
              <Route path="operations/cancellation-reasons" element={<CancellationReasonsPage />} />
              <Route path="finance/config/credit-types" element={<CreditTypesPage />} />
              <Route path="finance/config/approval-settings" element={<ApprovalSettingsPage />} />
              <Route path="settings/dropdown-lists" element={<DropdownListsSettings />} />
              <Route path="settings/tablets" element={<TabletsSettings />} />
              <Route path="company/outlets" element={<OutletsSettings />} />
              <Route path="settings/outlets" element={<RedirectToCompanyOutlets />} />

              <Route path="leave" element={<Leave />} />
              <Route path="leave/admin" element={<LeaveAdmin />} />
              <Route path="customers/reports/by-media" element={<CustomersByMedia />} />
              <Route path="customers/reports/by-marketing" element={<CustomersByMarketing />} />
              <Route path="marketing/reports/sales-by-media" element={<SalesByMedia />} />
              <Route path="marketing/reports/sales-by-marketing" element={<SalesByMarketing />} />
              <Route path="attendance/report" element={<AttendanceReport />} />
              <Route path="leave/calendar" element={<LeaveCalendar />} />
              <Route path="leave/ytd" element={<LeaveYtd />} />
              <Route path="leave/history" element={<LeaveHistory />} />
              <Route path="leave/approvers" element={<LeaveApprovers />} />
              <Route path="leave/types" element={<LeaveTypes />} />
              <Route path="hr/claims" element={<StaffClaimSubmission />} />
              <Route path="hr/claims/responses" element={<StaffClaimResponses />} />
              <Route path="hr/audit-trail" element={<AuditTrail />} />

              <Route path="salary" element={<PayrollLock screen="salary"><Salary /></PayrollLock>} />
              <Route path="salary/me" element={<PayrollLock screen="my-pay"><SalaryMe /></PayrollLock>} />

              {/* SAL-14 — the Point of Sale screen is retired; every sale is
                  made on New Invoice, so the old address opens it instead. */}
              <Route path="finance/pos" element={<RedirectToNewInvoice />} />
              <Route path="finance/pos/create-invoice" element={<FinanceCreateInvoice />} />
              <Route path="finance/pos/balance-payment" element={<FinanceBalancePayment />} />
              <Route path="finance/pos/create-usage" element={<FinanceCreateUsage />} />
              <Route path="finance/pos/void-usage" element={<FinanceVoidUsage />} />
              <Route path="finance/pos/credit-note" element={<FinanceCreditNote />} />
              <Route path="finance/pos/contra-invoice" element={<FinanceContraInvoice />} />
              <Route path="finance/pos/upgrade-invoice" element={<FinanceUpgradeInvoice />} />
              <Route path="finance/pos/extension" element={<FinanceExtensionRequest />} />
              <Route path="finance/pos/transfer" element={<FinanceHolderTransfer />} />
              <Route path="finance/pos/refund" element={<FinanceRefundRequest />} />
              <Route path="finance/pos/preview-invoice" element={<FinancePreviewInvoice />} />
              <Route path="finance/approvals" element={<FinanceApprovals />} />
              <Route path="finance/approvals/requests" element={<FinanceApprovalRequests />} />
              <Route path="finance/daily-close" element={<FinanceDailyClose />} />
              <Route path="finance/ihip" element={<FinanceIHIP />} />
              <Route path="finance/reports" element={<FinanceReports />} />
              <Route
                path="finance/reports/sales-by-customer"
                element={<RptSalesByCustomer />}
              />
              <Route
                path="finance/reports/collection-by-payment-types"
                element={<RptCollectionByPaymentTypes />}
              />
              <Route
                path="finance/reports/collection-by-customer"
                element={<RptCollectionByCustomer />}
              />
              <Route
                path="finance/reports/treatment-done-by-outlets"
                element={<RptTreatmentDoneByOutlets />}
              />
              <Route path="finance/reports/conversion" element={<RptConversion />} />
              <Route path="finance/reports/collection" element={<RptCollection />} />
              <Route path="finance/reports/sales-by-staff" element={<RptSalesByStaff />} />
              <Route path="finance/reports/consultation-results" element={<RptConsultationResults />} />
              <Route path="finance/reports/money-due-risk" element={<RptMoneyDueRisk />} />
              <Route path="finance/reports/top-customers-by-limit" element={<RptTopCustomersByLimit />} />
              <Route path="finance/reports/top-sellers" element={<RptTopSellers />} />
              <Route path="finance/reports/discounts-refunds-voids" element={<RptDiscountsRefundsVoids />} />
              <Route path="finance/reports/credit-notes" element={<RptCreditNotes />} />
              <Route path="finance/reports/gst" element={<RptGst />} />
              <Route
                path="finance/reports/item-sold-by-outlet"
                element={<RptItemSoldByOutlet />}
              />
              <Route path="finance/reports/item-sold-by-date" element={<RptItemSoldByDate />} />
              <Route
                path="finance/reports/ala-carte-by-outlet"
                element={<RptAlaCarteByOutlet />}
              />
              <Route
                path="finance/reports/ala-carte-by-beautician"
                element={<RptAlaCarteByBeautician />}
              />
              <Route path="finance/reports/unearned-revenue" element={<RptUnearnedRevenue />} />
              <Route path="finance/reports/void-usages" element={<RptVoidUsages />} />
              <Route path="finance/reports/expired-invoices" element={<RptExpiredInvoices />} />
              <Route
                path="finance/reports/items-sold-outlet-collection"
                element={<RptItemsSoldOutletCollection />}
              />
              <Route path="finance/sales" element={<FinanceSales />} />
              <Route path="finance/config/payment-types" element={<FinanceConfigPaymentTypes />} />
              <Route path="finance/config/payment-kinds" element={<FinanceConfigPaymentKinds />} />
              <Route path="finance/config/taxes" element={<FinanceConfigTaxes />} />
              <Route path="finance/search-invoices" element={<FinanceSearchInvoices />} />
              <Route path="finance/search-usages" element={<FinanceSearchUsages />} />
              <Route path="finance/search-credit-notes" element={<FinanceSearchCreditNotes />} />
              <Route path="hr/forms/damage-report" element={<HrDamageReport />} />
              <Route path="hr/forms/compliment-feedback" element={<HrComplimentFeedback />} />
              <Route path="hr/forms/submissions" element={<HrFormSubmissions />} />
              {/* GEN-06 — Forms groups of Sales, HR and Marketing (Customers' are above). */}
              <Route path="finance/forms/templates" element={<SalesFormTemplates />} />
              <Route path="finance/forms/templates/:id" element={<SalesFormTemplateEditor />} />
              <Route path="hr/forms/templates" element={<HrFormTemplates />} />
              <Route path="hr/forms/templates/:id" element={<HrFormTemplateEditor />} />
              <Route path="marketing/forms/templates" element={<MarketingFormTemplates />} />
              <Route path="marketing/forms/templates/:id" element={<MarketingFormTemplateEditor />} />
              <Route path="customers/forms/fill" element={<CustomersFillAForm />} />
              <Route path="customers/forms/fill/:templateId" element={<CustomersFillForm />} />
              <Route path="finance/forms/fill" element={<SalesFillAForm />} />
              <Route path="finance/forms/fill/:templateId" element={<SalesFillForm />} />
              <Route path="hr/forms/fill" element={<HrFillAForm />} />
              <Route path="hr/forms/fill/:templateId" element={<HrFillForm />} />
              <Route path="marketing/forms/fill" element={<MarketingFillAForm />} />
              <Route path="marketing/forms/fill/:templateId" element={<MarketingFillForm />} />
              <Route path="finance/forms/responses" element={<SalesFormResponses />} />
              <Route path="hr/forms/responses" element={<HrFormResponses />} />
              <Route path="marketing/forms/responses" element={<MarketingFormResponses />} />
              {/* GEN-06-22 — Forms › Approvals is a tab of Form Responses. */}
              <Route path="customers/forms/approvals" element={<RedirectToWaitingForms to="/customers/forms/responses" />} />
              <Route path="finance/forms/approvals" element={<RedirectToWaitingForms to="/finance/forms/responses" />} />
              <Route path="hr/forms/approvals" element={<RedirectToWaitingForms to="/hr/forms/responses" />} />
              <Route path="marketing/forms/approvals" element={<RedirectToWaitingForms to="/marketing/forms/responses" />} />
              <Route path="hr/yakson-ai/history" element={<YaksonAiHistory />} />
              <Route path="hr/yakson-ai/knowledge" element={<YaksonAiKnowledge />} />
              <Route path="hr/yakson-ai/qa" element={<YaksonAiQa />} />
              <Route path="hr/yakson-ai/history/qa" element={<RedirectToQa />} />
              <Route path="hr/yakson-ai/history/unanswered" element={<RedirectToUnanswered />} />
              <Route path="finance/xero" element={<FinanceXero />} />

              <Route path="lab/drawing" element={<LabDrawing />} />
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/en/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}
